import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { customerCabinetApi } from '../features/customerCabinet/api';
import { carrierCabinetApi } from '../features/carrierCabinet/api';
import { useAuth } from '../state/auth';
import { i18n } from './config';
import { localizeDocument, observeLocalizedDocument } from './dom';
import { isAppLanguage, readStoredLanguage, storeLanguage, type AppLanguage } from './locale';
import { setDisplayCurrency, setRuntimeTimezone } from './preferences';
import { setRuntimeLanguage } from './runtime';

interface LanguageContextValue {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const [language, setLanguageState] = useState<AppLanguage>(() => readStoredLanguage());
  const localChangeVersion = useRef(0);

  const applyLanguage = useCallback((nextLanguage: AppLanguage) => {
    setRuntimeLanguage(nextLanguage);
    storeLanguage(nextLanguage);
    document.documentElement.lang = nextLanguage;
    void i18n.changeLanguage(nextLanguage);
    setLanguageState(nextLanguage);
  }, []);

  const setLanguage = useCallback((nextLanguage: AppLanguage) => {
    localChangeVersion.current += 1;
    applyLanguage(nextLanguage);
    if (status === 'authenticated') {
      if (user?.has_customer_profile) {
        void customerCabinetApi.updateProfile({ language: nextLanguage }).catch(() => undefined);
      }
      if (user?.has_carrier_profile) {
        void carrierCabinetApi.updateProfile({ language: nextLanguage }).catch(() => undefined);
      }
    }
  }, [applyLanguage, status, user?.has_carrier_profile, user?.has_customer_profile]);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'ru' ? 'en' : 'ru');
  }, [language, setLanguage]);

  useEffect(() => {
    setRuntimeLanguage(language);
    document.documentElement.lang = language;
    void i18n.changeLanguage(language);
    window.requestAnimationFrame(localizeDocument);
  }, [language]);

  useEffect(() => observeLocalizedDocument(), []);

  useEffect(() => {
    if (status !== 'authenticated') return undefined;

    let active = true;
    const versionAtStart = localChangeVersion.current;

    if (user?.active_role === 'carrier' && user.has_carrier_profile) {
      void carrierCabinetApi.profile().then((profile) => {
        if (!active || localChangeVersion.current !== versionAtStart) return;
        if (profile.timezone) setRuntimeTimezone(profile.timezone);
        if (profile.display_currency) setDisplayCurrency(profile.display_currency);
        if (profile.language && isAppLanguage(profile.language)) applyLanguage(profile.language);
      }).catch(() => undefined);
    } else if (user?.has_customer_profile) {
      void customerCabinetApi.profile().then((profile) => {
        if (!active || localChangeVersion.current !== versionAtStart) return;
        if (profile.timezone) setRuntimeTimezone(profile.timezone);
        if (profile.display_currency) setDisplayCurrency(profile.display_currency);
        if (isAppLanguage(profile.language)) applyLanguage(profile.language);
      }).catch(() => undefined);
    }

    return () => {
      active = false;
    };
  }, [applyLanguage, status, user?.active_role, user?.has_carrier_profile, user?.has_customer_profile, user?.id]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'lovo.interface-language' && (event.newValue === 'ru' || event.newValue === 'en')) {
        setLanguage(event.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [setLanguage]);

  const value = useMemo(() => ({ language, setLanguage, toggleLanguage }), [language, setLanguage, toggleLanguage]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used inside LanguageProvider');
  return context;
}
